import { useEffect, useState } from 'react';
import { Button, Row, Stack } from 'react-bootstrap';
import { Trans } from 'react-i18next';
import OtpInput from 'react-otp-input';

import { Api } from 'src/models/Api.model';
import { AxiosResponseWithRefreshToken } from 'src/models/GalenData/Request.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

interface MFALoginProps {
  xRequestToken: string;
  validateUserRole: (
    res: AxiosResponseWithRefreshToken<GalenDataUser>,
  ) => boolean;
  onVerificationSuccess: (
    res: AxiosResponseWithRefreshToken<GalenDataUser>,
  ) => void;
  setIsLoggingIn: (value: boolean) => void;
  isLoggingIn: boolean;
}

const MFALogin = ({
  xRequestToken,
  validateUserRole,
  onVerificationSuccess,
  setIsLoggingIn,
  isLoggingIn,
}: MFALoginProps) => {
  const { tPages, tCommon, language } = useUiTranslation();
  const [otp, setOtp] = useState('');
  const [countdownSeconds, setCountdownSeconds] = useState(30);
  const { handleHttpRequestError } = useHandleHttpRequestError();

  useEffect(() => {
    if (countdownSeconds > 0) {
      const intervalId = setInterval(() => {
        setTimeout(() => {
          setCountdownSeconds((prevSeconds) => prevSeconds - 1);
        }, 500);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [countdownSeconds]);

  const handleVerifyOTP = async (otp: string) => {
    try {
      const res = await Api.remote.verifyMFACode(otp, xRequestToken);

      if (!validateUserRole(res)) {
        setIsLoggingIn(false);
        return;
      }

      onVerificationSuccess(res);
    } catch (err) {
      const errorMessage = handleHttpRequestError(err);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const handleResendCode = async () => {
    try {
      await Api.remote.sendMFACode(xRequestToken);
      setCountdownSeconds(30);
    } catch (err) {
      const errorMessage = handleHttpRequestError(err);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  return (
    <Stack className="justify-content-center p-4" gap={4}>
      <Stack className="text-center" gap={2}>
        <h2>{tPages('loginLogout.verifyAccount')}</h2>
        <p>{tPages('loginLogout.verifyAccountCode')}</p>
      </Stack>
      <Stack gap={4}>
        <OtpInput
          inputType="tel"
          containerStyle={{ display: 'flex', justifyContent: 'center' }}
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span className="mx-2">-</span>}
          inputStyle={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.25rem',
            border: '1px solid #ced4da',
            textAlign: 'center',
            fontSize: '1.5rem',
          }}
          renderInput={(props) => <input {...props} />}
          shouldAutoFocus
        />
        <div>
          {isLoggingIn && (
            <p className="alert alert-info">
              {tPages('loginLogout.loggingInPleaseWait')}
            </p>
          )}
        </div>
        <Row>
          <Button
            onClick={() => handleVerifyOTP(otp)}
            disabled={otp.length < 6}
          >
            {tPages('loginLogout.confirm')}
          </Button>
        </Row>
        <Stack className="justify-content-center">
          {countdownSeconds > 0 ? (
            <p className="text-center">
              <Trans
                i18nKey="ui.pages.loginLogout.resendCodeInSeconds"
                values={{ countdownSeconds }}
                components={[<strong>{countdownSeconds}</strong>]}
              />
            </p>
          ) : (
            <Stack direction="horizontal" className="justify-content-center">
              <span>{tPages('loginLogout.didNotReceiveCode')}</span>
              <Button variant="link" className="p-0" onClick={handleResendCode}>
                {tPages('loginLogout.resendCode')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default MFALogin;
